<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import {
  dayKey,
  eventClasses,
  eventSpan,
  formatDuration,
  formatTimeRange,
  isAllDayEvent,
  MINUTES_PER_DAY,
  MINUTES_PER_HOUR,
  minuteOfDay,
  packLanes,
  timedSegments,
  timeZoneLabel,
  useCalendarFormatters,
} from '@/composables/event-calendar'
import { pxToRem, remToPx } from '@/lib/utils'
import { ABSENCE_ICON, calendarDragModeEnum, eventTypeEnum } from '@/stores/schedule.store'
import { useWebSiteStore } from '@/stores/web-site.store'

const ALL_DAY_LANE_HEIGHT = 1.5
const MAX_COLLAPSED_LANES = 2
const DEFAULT_CREATE_MINUTES = 60
const SCROLL_CONTEXT_MINUTES = 90
const HOURS_PER_DAY = 24
const COMPACT_BLOCK_MINUTES = 45
const DRAG_THRESHOLD_PX = 4
const TAP_THRESHOLD_PX = 10
const WHEEL_ZOOM_FACTOR = 1.1
const LONG_PRESS_MS = 500
const BODY_BOTTOM_PADDING = 0.5

const props = defineProps({
  days: {
    type: Array,
    required: true,
  },
  events: {
    type: Array,
    required: true,
  },
  now: {
    type: Object,
    required: true,
  },
  scrollHour: {
    type: Number,
    default: 8,
  },
  slotMinutes: {
    type: Number,
    default: 30,
  },
  hourHeight: {
    type: Number,
    default: null,
  },
  initialScrollTop: {
    type: Number,
    default: null,
  },
  canEdit: {
    type: Function,
    default: () => false,
  },
  compact: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['event-click', 'create', 'event-change', 'day-click', 'scroll', 'zoom'])

const { t, locale } = useI18n()
const webSiteStore = useWebSiteStore()
const formatters = computed(() => useCalendarFormatters(locale.value))

const scrollRef = ref(null)
const stickyRef = ref(null)
const fitHourHeight = ref(0)
let resizeObserver = null
const bodyRef = ref(null)
const columnsRef = ref(null)
const drag = ref(null)
const allDayExpanded = ref(false)

const hours = Array.from({ length: HOURS_PER_DAY }, (_, hour) => hour)
const rangeStart = computed(() => props.days[0])
const todayKey = computed(() => dayKey(props.now))
const weekdayFormatter = computed(() => (props.compact && props.days.length > 3 ? formatters.value.weekdayNarrow : formatters.value.weekday))
const tzLabel = computed(() => timeZoneLabel(formatters.value))

const allDay = computed(() => packLanes(
  props.events
    .filter(isAllDayEvent)
    .map(event => eventSpan(event, rangeStart.value, props.days.length))
    .filter(Boolean),
))
const allDayCollapsible = computed(() => allDay.value.laneCount > MAX_COLLAPSED_LANES + 1)
const allDayLaneLimit = computed(() => (allDayCollapsible.value && !allDayExpanded.value ? MAX_COLLAPSED_LANES : allDay.value.laneCount))
const allDayBars = computed(() => allDay.value.items.filter(item => item.lane < allDayLaneLimit.value))
const allDayHidden = computed(() => props.days.map((_, index) =>
  allDay.value.items.filter(item => item.lane >= allDayLaneLimit.value && item.from <= index && item.to >= index).length,
))
const allDayRows = computed(() => Math.max(allDayLaneLimit.value, 1) + (allDayHidden.value.some(Boolean) ? 1 : 0))

const timedEvents = computed(() => props.events.filter(event => !isAllDayEvent(event)))
const columns = computed(() => props.days.map(day => {
  const dayEnd = day.add(1, 'day')
  const dayEvents = timedEvents.value.filter(event => event.startAt.isBefore(dayEnd) && event.endAt.isAfter(day))
  const key = dayKey(day)
  return {
    day,
    key,
    today: key === todayKey.value,
    past: day.isBefore(props.now, 'day'),
    rest: webSiteStore.isRestDay(day),
    segments: timedSegments(dayEvents, day, 0, MINUTES_PER_DAY),
  }
}))

const rowHeight = computed(() => props.hourHeight ?? fitHourHeight.value)
const minutesToRem = (minutes) => (minutes / MINUTES_PER_HOUR) * rowHeight.value
const nowMinute = computed(() => minuteOfDay(props.now))

const verticalStyle = (from, to) => ({
  top: `${minutesToRem(from)}rem`,
  height: `calc(${minutesToRem(to - from)}rem - 0.1875rem)`,
})

const segmentStyle = (segment) => ({
  ...verticalStyle(segment.from, segment.extent),
  left: `calc(${segment.left} * (100% - var(--event-gutter)) + 0.125rem)`,
  width: `calc(${segment.width} * (100% - var(--event-gutter)) - 0.25rem)`,
  '--event-color': segment.event.color,
  '--event-layer': segment.layer,
})

const barStyle = (item) => ({
  left: `calc(${(item.from / props.days.length) * 100}% + 0.125rem)`,
  width: `calc(${((item.to - item.from + 1) / props.days.length) * 100}% - 0.25rem)`,
  top: `${item.lane * ALL_DAY_LANE_HEIGHT}rem`,
  '--event-color': item.event.color,
})

const isCompactBlock = (segment) => segment.extent - segment.from < COMPACT_BLOCK_MINUTES
const eventTime = (event) => formatTimeRange(formatters.value, event.startAt, event.endAt)
const eventDateRange = (event) =>
  `${formatters.value.dayMonth.format(event.startAt.toDate())} – ${formatters.value.dayMonth.format(event.endAt.subtract(1, 'ms').toDate())}`
const eventTitle = (event) => event.title || t('common.staff.absence')
const isEditable = (event) => !event.is_project_root && props.canEdit(event)

const minuteAtClientY = (clientY) =>
  (pxToRem(clientY - bodyRef.value.getBoundingClientRect().top) / rowHeight.value) * MINUTES_PER_HOUR

const dayIndexAtClientX = (clientX) => {
  const rect = columnsRef.value.getBoundingClientRect()
  const index = Math.floor(((clientX - rect.left) / rect.width) * props.days.length)
  return Math.min(props.days.length - 1, Math.max(0, index))
}

const floorToSlot = (minutes) => Math.floor(minutes / props.slotMinutes) * props.slotMinutes
const roundToSlot = (minutes) => Math.round(minutes / props.slotMinutes) * props.slotMinutes
const clampMinute = (minutes) => Math.min(MINUTES_PER_DAY, Math.max(0, minutes))

const draggedRange = (state) => {
  const { event } = state
  if (state.mode === calendarDragModeEnum.MOVE) {
    return {
      start: event.startAt.add(state.dayDelta, 'day').add(state.delta, 'minute'),
      end: event.endAt.add(state.dayDelta, 'day').add(state.delta, 'minute'),
    }
  }
  const end = event.endAt.add(state.delta, 'minute')
  const minimumEnd = event.startAt.add(props.slotMinutes, 'minute')
  return { start: event.startAt, end: end.isAfter(minimumEnd) ? end : minimumEnd }
}

const dragPreview = computed(() => {
  const state = drag.value
  if (!state?.moved || state.mode == null || state.mode === calendarDragModeEnum.CREATE) {
    return null
  }
  const { start, end } = draggedRange(state)
  const dayIndex = start.startOf('day').diff(rangeStart.value, 'day')
  if (dayIndex < 0 || dayIndex >= props.days.length) {
    return null
  }
  const day = props.days[dayIndex]
  const from = clampMinute(start.diff(day, 'minute'))
  const to = clampMinute(end.diff(day, 'minute'))
  return {
    dayIndex,
    event: state.event,
    label: formatTimeRange(formatters.value, start, end),
    style: { ...verticalStyle(from, Math.max(to, from + props.slotMinutes)), '--event-color': state.event.color },
  }
})

const createGhost = computed(() => {
  const state = drag.value
  if (state?.mode !== calendarDragModeEnum.CREATE || state.pointerType === 'touch') {
    return null
  }
  const day = props.days[state.dayIndex]
  return {
    dayIndex: state.dayIndex,
    label: formatTimeRange(formatters.value, day.add(state.from, 'minute'), day.add(state.to, 'minute')),
    style: verticalStyle(state.from, state.to),
  }
})

const onEscape = (e) => {
  if (e.key === 'Escape') {
    stopDrag()
  }
}

const onPointerMove = (e) => {
  const state = drag.value
  if (!state) {
    return
  }
  const distance = Math.hypot(e.clientX - state.originX, e.clientY - state.originY)
  const threshold = state.pointerType === 'touch' ? TAP_THRESHOLD_PX : DRAG_THRESHOLD_PX
  if (!state.moved && distance < threshold) {
    return
  }
  if (state.pointerType === 'touch') {
    stopDrag()
    return
  }
  state.moved = true
  if (state.mode === calendarDragModeEnum.CREATE) {
    const current = clampMinute(floorToSlot(minuteAtClientY(e.clientY)))
    state.from = Math.min(state.anchor, current)
    state.to = clampMinute(Math.max(state.anchor, current) + props.slotMinutes)
    return
  }
  if (state.mode == null) {
    return
  }
  state.delta = roundToSlot(minuteAtClientY(e.clientY) - state.originMinute)
  if (state.mode === calendarDragModeEnum.MOVE) {
    state.dayDelta = dayIndexAtClientX(e.clientX) - state.originDayIndex
  }
}

const onPointerUp = () => {
  const state = drag.value
  stopDrag()
  if (!state) {
    return
  }
  if (state.pointerType === 'touch' && Date.now() - state.startedAt > LONG_PRESS_MS) {
    return
  }
  if (state.mode === calendarDragModeEnum.CREATE) {
    if (state.pointerType === 'touch' && state.moved) {
      return
    }
    const day = props.days[state.dayIndex]
    const to = state.moved ? state.to : clampMinute(state.from + DEFAULT_CREATE_MINUTES)
    emit('create', { start: day.add(state.from, 'minute'), end: day.add(to, 'minute') })
    return
  }
  if (!state.moved) {
    emit('event-click', state.event)
    return
  }
  if (state.mode != null && (state.delta || state.dayDelta)) {
    emit('event-change', { event: state.event, ...draggedRange(state) })
  }
}

function stopDrag() {
  drag.value = null
  window.removeEventListener('pointermove', onPointerMove)
  window.removeEventListener('pointerup', onPointerUp)
  window.removeEventListener('pointercancel', stopDrag)
  window.removeEventListener('keydown', onEscape)
}

const startDrag = (e, state) => {
  drag.value = { ...state, pointerType: e.pointerType, originX: e.clientX, originY: e.clientY, startedAt: Date.now(), moved: false }
  window.addEventListener('pointermove', onPointerMove)
  window.addEventListener('pointerup', onPointerUp)
  window.addEventListener('pointercancel', stopDrag)
  window.addEventListener('keydown', onEscape)
}

const onColumnPointerDown = (e, dayIndex) => {
  if (e.button !== 0) {
    return
  }
  if (e.pointerType !== 'touch') {
    e.preventDefault()
  }
  const from = Math.min(clampMinute(floorToSlot(minuteAtClientY(e.clientY))), MINUTES_PER_DAY - props.slotMinutes)
  startDrag(e, { mode: calendarDragModeEnum.CREATE, dayIndex, anchor: from, from, to: from + props.slotMinutes })
}

const onEventPointerDown = (e, event, dayIndex, mode = calendarDragModeEnum.MOVE) => {
  if (e.button !== 0) {
    return
  }
  e.stopPropagation()
  const editable = e.pointerType !== 'touch' && isEditable(event)
  if (editable) {
    e.preventDefault()
  }
  startDrag(e, {
    mode: editable ? mode : null,
    event,
    originDayIndex: dayIndex,
    originMinute: minuteAtClientY(e.clientY),
    delta: 0,
    dayDelta: 0,
  })
}

const onBarPointerDown = (e, event) => {
  if (e.button !== 0) {
    return
  }
  e.stopPropagation()
  startDrag(e, { mode: null, event })
}

const onAllDayCellClick = (day) => {
  emit('create', { start: day, end: day.add(1, 'day') })
}

let pinch = null
const touchDistance = (touches) => Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY)

const anchorZoom = (minute, offsetY) => {
  nextTick(() => {
    if (scrollRef.value) {
      scrollRef.value.scrollTop = bodyRef.value.offsetTop + remToPx(minutesToRem(minute)) - offsetY
    }
  })
}

const onTouchStart = (e) => {
  if (e.touches.length !== 2) {
    return
  }
  stopDrag()
  const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2
  pinch = {
    distance: touchDistance(e.touches),
    hourHeight: rowHeight.value,
    minute: minuteAtClientY(midY),
    offsetY: midY - scrollRef.value.getBoundingClientRect().top,
  }
}

const onTouchMove = (e) => {
  if (!pinch || e.touches.length !== 2) {
    return
  }
  e.preventDefault()
  emit('zoom', pinch.hourHeight * (touchDistance(e.touches) / pinch.distance))
  anchorZoom(pinch.minute, pinch.offsetY)
}

const onTouchEnd = (e) => {
  if (e.touches.length < 2) {
    pinch = null
  }
}

const onWheel = (e) => {
  if (!e.ctrlKey) {
    return
  }
  e.preventDefault()
  const minute = minuteAtClientY(e.clientY)
  const offsetY = e.clientY - scrollRef.value.getBoundingClientRect().top
  emit('zoom', e.deltaY < 0 ? rowHeight.value * WHEEL_ZOOM_FACTOR : rowHeight.value / WHEEL_ZOOM_FACTOR)
  anchorZoom(minute, offsetY)
}

const defaultScrollMinute = () => {
  if (columns.value.some(column => column.today)) {
    return nowMinute.value - SCROLL_CONTEXT_MINUTES
  }
  const firstStarts = columns.value.flatMap(column => column.segments.map(segment => segment.from))
  return (firstStarts.length ? Math.min(...firstStarts) : props.scrollHour * MINUTES_PER_HOUR) - MINUTES_PER_HOUR / 2
}

const measureFitHeight = () => {
  const available = pxToRem(scrollRef.value.clientHeight - stickyRef.value.offsetHeight) - BODY_BOTTOM_PADDING
  fitHourHeight.value = Math.max(0, available / HOURS_PER_DAY)
}

onMounted(() => {
  measureFitHeight()
  resizeObserver = new ResizeObserver(measureFitHeight)
  resizeObserver.observe(scrollRef.value)
  resizeObserver.observe(stickyRef.value)
  scrollRef.value.addEventListener('touchmove', onTouchMove, { passive: false })
  scrollRef.value.scrollTop = props.initialScrollTop
    ?? remToPx(minutesToRem(clampMinute(defaultScrollMinute())))
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  stopDrag()
  scrollRef.value?.removeEventListener('touchmove', onTouchMove)
})
</script>
<template>
  <div
    ref="scrollRef"
    class="cal-grid"
    :class="{ 'cal-grid--compact': compact, 'cal-grid--dragging': drag?.moved }"
    :style="{ '--day-count': days.length, '--hour-height': `${rowHeight}rem` }"
    @scroll.passive="emit('scroll', scrollRef.scrollTop)"
    @wheel="onWheel"
    @touchstart.passive="onTouchStart"
    @touchend.passive="onTouchEnd"
    @touchcancel.passive="onTouchEnd"
  >
    <div ref="stickyRef" class="cal-grid__sticky">
      <div class="cal-grid__head">
        <div class="cal-grid__gutter cal-grid__tz">
          {{ tzLabel }}
        </div>
        <div
          v-for="column in columns"
          :key="column.key"
          class="cal-grid__day-head"
          :class="{ 'cal-grid__day-head--today': column.today, 'cal-grid__day-head--past': column.past }"
        >
          <span class="cal-grid__weekday">{{ weekdayFormatter.format(column.day.toDate()) }}</span>
          <button
            type="button"
            class="cal-grid__date"
            @click="emit('day-click', column.day)"
          >
            {{ column.day.date() }}
          </button>
        </div>
      </div>
      <div class="cal-grid__allday">
        <div class="cal-grid__gutter cal-grid__allday-gutter">
          <q-btn
            v-if="allDayCollapsible"
            flat
            round
            dense
            size="sm"
            :icon="allDayExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"
            @click="allDayExpanded = !allDayExpanded"
          >
            <q-tooltip>{{ allDayExpanded ? t('common.showLess') : t('common.showMore') }}</q-tooltip>
          </q-btn>
        </div>
        <div
          class="cal-grid__allday-track"
          :style="{ height: `${allDayRows * ALL_DAY_LANE_HEIGHT + 0.25}rem` }"
        >
          <button
            v-for="column in columns"
            :key="column.key"
            type="button"
            class="cal-grid__allday-cell"
            :data-day="column.key"
            :class="{ 'cal-grid__allday-cell--rest': column.rest, 'cal-grid__allday-cell--today': column.today }"
            @click="onAllDayCellClick(column.day)"
          />
          <transition-group name="cal-event-fade">
            <div
              v-for="item in allDayBars"
              :key="`${item.event.id}-${item.from}`"
              :data-event-id="item.event.id"
              class="cal-event cal-event--bar"
              :class="[
                eventClasses(item.event),
                { 'cal-event--clip-start': item.clippedStart, 'cal-event--clip-end': item.clippedEnd },
              ]"
              :style="barStyle(item)"
              @pointerdown="onBarPointerDown($event, item.event)"
            >
              <q-icon
                v-if="item.event.type === eventTypeEnum.ABSENCE"
                :name="ABSENCE_ICON"
                size="0.875rem"
              />
              <span class="cal-event__title">{{ eventTitle(item.event) }}</span>
              <span v-if="item.event.subtitle" class="cal-event__subtitle">{{ item.event.subtitle }}</span>
              <span v-if="!compact && item.event.status" class="cal-event__subtitle">· {{ item.event.status }}</span>
              <q-tooltip v-if="!compact" :delay="500">
                <div class="text-weight-bold">{{ eventTitle(item.event) }}</div>
                <div>{{ eventDateRange(item.event) }}</div>
                <div>{{ item.event.status }}</div>
              </q-tooltip>
            </div>
          </transition-group>
          <template v-for="(hidden, index) in allDayHidden" :key="`hidden-${index}`">
            <button
              v-if="hidden"
              type="button"
              class="cal-grid__allday-more"
              :style="{
                left: `${(index / days.length) * 100}%`,
                width: `${100 / days.length}%`,
                top: `${allDayLaneLimit * ALL_DAY_LANE_HEIGHT}rem`,
              }"
              @click="allDayExpanded = true"
            >
              {{ t('common.moreCount', { count: hidden }) }}
            </button>
          </template>
        </div>
      </div>
    </div>

    <div
      ref="bodyRef"
      class="cal-grid__body"
      :style="{ height: `${HOURS_PER_DAY * rowHeight}rem` }"
    >
      <div class="cal-grid__gutter cal-grid__hours">
        <span
          v-for="hour in hours"
          :key="hour"
          class="cal-grid__hour"
          :style="{ top: `${hour * rowHeight}rem` }"
        >
          {{ hour === 0 ? '' : `${String(hour).padStart(2, '0')}:00` }}
        </span>
      </div>
      <div ref="columnsRef" class="cal-grid__columns">
        <div
          v-for="(column, dayIndex) in columns"
          :key="column.key"
          class="cal-grid__column"
          :data-day="column.key"
          :class="{ 'cal-grid__column--rest': column.rest, 'cal-grid__column--today': column.today }"
          @pointerdown="onColumnPointerDown($event, dayIndex)"
        >
          <div
            v-if="column.past || column.today"
            class="cal-grid__past"
            :style="{ height: column.past ? '100%' : `${Math.max(0, minutesToRem(nowMinute))}rem` }"
          />
          <transition-group name="cal-event-fade">
            <div
              v-for="segment in column.segments"
              :key="`${segment.event.id}-${column.key}`"
              :data-event-id="segment.event.id"
              class="cal-event cal-event--timed"
              :class="[
                eventClasses(segment.event),
                {
                  'cal-event--compact': isCompactBlock(segment),
                  'cal-event--editable': isEditable(segment.event),
                  'cal-event--drag-source': drag?.moved && drag.event?.id === segment.event.id && drag.mode != null,
                  'cal-event--clip-start': segment.clippedStart,
                  'cal-event--clip-end': segment.clippedEnd,
                },
              ]"
              :style="segmentStyle(segment)"
              @pointerdown="onEventPointerDown($event, segment.event, dayIndex)"
            >
              <span
                v-if="segment.event.type === eventTypeEnum.ALLOCATION"
                class="cal-event__dot"
                :style="{ backgroundColor: segment.event.resourceColor }"
              />
              <div class="cal-event__content">
                <span class="cal-event__title">
                  <q-icon
                    v-if="segment.event.type === eventTypeEnum.ABSENCE"
                    :name="ABSENCE_ICON"
                    size="0.875rem"
                  />
                  {{ eventTitle(segment.event) }}<template v-if="isCompactBlock(segment)">, {{ formatters.time.format(segment.event.startAt.toDate()) }}</template>
                </span>
                <template v-if="!isCompactBlock(segment)">
                  <span v-if="segment.event.subtitle" class="cal-event__subtitle">{{ segment.event.subtitle }}</span>
                  <span class="cal-event__time">{{ eventTime(segment.event) }} · {{ formatDuration(segment.event.startAt, segment.event.endAt) }}</span>
                  <span v-if="!compact" class="cal-event__status">
                    <q-icon :name="segment.event.confirmed ? 'mdi-check-circle' : 'mdi-progress-clock'" size="0.75rem" />
                    {{ segment.event.status }}
                  </span>
                </template>
              </div>
              <div
                v-if="isEditable(segment.event) && !segment.clippedEnd"
                class="cal-event__handle"
                @pointerdown="onEventPointerDown($event, segment.event, dayIndex, calendarDragModeEnum.RESIZE_END)"
              />
              <q-tooltip v-if="!compact && !drag" :delay="500">
                <div class="text-weight-bold">{{ eventTitle(segment.event) }}</div>
                <div v-if="segment.event.subtitle">{{ segment.event.subtitle }}</div>
                <div>{{ eventTime(segment.event) }} · {{ formatDuration(segment.event.startAt, segment.event.endAt) }}</div>
                <div>{{ segment.event.status }}</div>
              </q-tooltip>
            </div>
          </transition-group>
          <div
            v-if="dragPreview?.dayIndex === dayIndex"
            class="cal-event cal-event--timed cal-event--preview"
            :class="eventClasses(dragPreview.event)"
            :style="dragPreview.style"
          >
            <div class="cal-event__content">
              <span class="cal-event__title">{{ eventTitle(dragPreview.event) }}</span>
              <span class="cal-event__time">{{ dragPreview.label }}</span>
            </div>
          </div>
          <div
            v-if="createGhost?.dayIndex === dayIndex"
            class="cal-event cal-event--timed cal-event--unconfirmed cal-event--preview"
            :style="createGhost.style"
          >
            <div class="cal-event__content">
              <span class="cal-event__title">{{ t('common.event', 1) }}</span>
              <span class="cal-event__time">{{ createGhost.label }}</span>
            </div>
          </div>
          <div
            v-if="column.today"
            class="cal-grid__now"
            :style="{ top: `${minutesToRem(nowMinute)}rem` }"
          />
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped lang="scss">
.cal-grid {
  --gutter: 3.5rem;
  position: relative;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  touch-action: pan-x pan-y;
  scrollbar-width: thin;
  scrollbar-color: color-mix(in srgb, var(--q-primary) 30%, transparent) transparent;
}

.cal-grid--compact {
  --gutter: 2.75rem;
}

.cal-grid--dragging,
.cal-grid--dragging * {
  cursor: grabbing !important;
}

.cal-grid__gutter {
  flex-shrink: 0;
  width: var(--gutter);
}

// --- Sticky header ---
.cal-grid__sticky {
  position: sticky;
  top: 0;
  z-index: 4;
  background: var(--cal-surface);
  border-bottom: 1px solid var(--cal-border);
  box-shadow: 0 0.25rem 0.5rem -0.375rem rgba(0, 0, 0, 0.12);
}

.cal-grid__head {
  display: flex;
}

.cal-grid__tz {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 0.25rem;
  font-size: 0.625rem;
  color: var(--cal-text-muted);
}

.cal-grid__day-head {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.125rem;
  padding: 0.5rem 0 0.25rem;
}

.cal-grid__weekday {
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--cal-text-muted);
}

.cal-grid__date {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--cal-text);
  font: inherit;
  font-size: 1.0625rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.15s ease, transform 0.15s ease;

  &:hover {
    background: color-mix(in srgb, var(--q-primary) 10%, transparent);
  }

  &:active {
    transform: scale(0.92);
  }
}

.cal-grid--compact .cal-grid__date {
  width: 1.625rem;
  height: 1.625rem;
  font-size: 0.9375rem;
}

.cal-grid__day-head--past .cal-grid__date {
  color: var(--cal-text-muted);
}

.cal-grid__day-head--today {
  .cal-grid__weekday {
    color: var(--q-primary);
  }

  .cal-grid__date,
  .cal-grid__date:hover {
    background: var(--q-primary);
    color: white;
    box-shadow: 0 0.375rem 0.875rem -0.375rem var(--q-primary);
  }
}

// --- All-day lane ---
.cal-grid__allday {
  display: flex;
}

.cal-grid__allday-gutter {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 0.125rem;
}

.cal-grid__allday-track {
  position: relative;
  flex: 1;
  display: flex;
  min-height: 1.75rem;
  transition: height 0.2s ease;
}

.cal-grid__allday-cell {
  flex: 1 1 0;
  padding: 0;
  border: 0;
  border-left: 1px solid var(--cal-line);
  background: transparent;
  cursor: pointer;

  &:hover {
    background: color-mix(in srgb, var(--q-primary) 5%, transparent);
  }
}

.cal-grid__allday-cell--rest {
  background: var(--cal-rest-day);
}

.cal-grid__allday-cell--today {
  background: var(--cal-today);
}

.cal-grid__allday-more {
  position: absolute;
  height: 1.375rem;
  padding: 0 0.5rem;
  border: 0;
  border-radius: 0.375rem;
  background: transparent;
  color: var(--cal-text-muted);
  font: inherit;
  font-size: 0.6875rem;
  font-weight: 600;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: color-mix(in srgb, var(--q-primary) 8%, transparent);
    color: var(--q-primary);
  }
}

// --- Time body ---
.cal-grid__body {
  position: relative;
  display: flex;
  box-sizing: content-box;
  padding-bottom: 0.5rem;
}

.cal-grid__hours {
  position: relative;
}

.cal-grid__hour {
  position: absolute;
  right: 0.5rem;
  transform: translateY(-50%);
  font-size: 0.6875rem;
  font-variant-numeric: tabular-nums;
  color: var(--cal-text-muted);
}

.cal-grid--compact .cal-grid__hour {
  right: 0.25rem;
  font-size: 0.625rem;
}

.cal-grid__columns {
  position: relative;
  flex: 1;
  display: flex;
  background-image: repeating-linear-gradient(
    to bottom,
    var(--cal-line) 0 1px,
    transparent 1px var(--hour-height)
  );
}

.cal-grid__column {
  position: relative;
  flex: 1 1 0;
  --event-gutter: 1.5rem;
  min-width: 0;
  border-left: 1px solid var(--cal-line);
  touch-action: pan-x pan-y;
  isolation: isolate;
}

.cal-grid__column--rest {
  background: var(--cal-rest-day);
}

.cal-grid__column--today {
  background: var(--cal-today);
}

.cal-grid__past {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  background: var(--cal-past);
  pointer-events: none;
}

.cal-grid__now {
  position: absolute;
  left: -0.0625rem;
  right: 0;
  z-index: 60;
  height: 0;
  border-top: 2px solid var(--cal-now);
  pointer-events: none;

  &::before {
    content: '';
    position: absolute;
    top: -0.40625rem;
    left: -0.375rem;
    width: 0.75rem;
    height: 0.75rem;
    border-radius: 50%;
    background: var(--cal-now);
    box-shadow: 0 0 0 0.1875rem color-mix(in srgb, var(--cal-now) 25%, transparent);
    animation: cal-now-pulse 2.4s ease-out infinite;
  }
}

@keyframes cal-now-pulse {
  0% {
    box-shadow: 0 0 0 0 color-mix(in srgb, var(--cal-now) 45%, transparent);
  }

  70%,
  100% {
    box-shadow: 0 0 0 0.5rem transparent;
  }
}
</style>
